import { useEffect, useRef } from 'react';
import { NudosGenericModal, NudosModalContent } from '../../../../../components/NudosComponents';
import {
  IlogisticServiceDetails,
  IlogisticServiceDetailsToolsData,
  IlogisticServiceTrelloCardData
} from '../../../../../types/services';
import useDeliveredToolsState, { SECTIONS_STEPS } from 'state/useDeliveredToolsState';
import SelectedToolsSection from './components/sections/SelectedToolsSection';
import ConfirmationSection from './components/sections/ConfirmationSection';
import AddAdditionalToolsSection from './components/sections/AddAdditionalToolsSection';

interface OffboardingToWarehouseModalProps {
  cardData: IlogisticServiceTrelloCardData;
  serviceDetails: IlogisticServiceDetails;
  closeModalCallback: () => void;
  closeModalAfterSubmitCallback: () => void;
  isUpdateTools?: boolean;
}

export const OffboardingToWarehouseModal = ({
  closeModalCallback,
  closeModalAfterSubmitCallback,
  cardData,
  serviceDetails,
  isUpdateTools = false
}: OffboardingToWarehouseModalProps) => {
  const {
    currentStep,
    setAvailableTools,
    setSelectedTools,
    setUnselectedTools,
    setMarkedNotDeliveredToolIds,
    resetState
  } = useDeliveredToolsState();
  const hasInitializedRef = useRef<boolean>(false);

  /**
   * Inicializa los estados de las herramientas disponibles y organiza las herramientas
   * seleccionadas y no seleccionadas según su estado de entrega cuando es una actualización.
   * @param tools - Lista de herramientas del servicio
   * @param isUpdate - Indica si es una actualización de herramientas
   */
  const initializeToolsState = (tools?: IlogisticServiceDetailsToolsData[] | null, isUpdate?: boolean) => {
    if (!tools || hasInitializedRef.current) return;

    if (isUpdate) {
      setAvailableTools(tools);

      const selectedIndices: number[] = [];
      const unselectedIndices: number[] = [];

      tools.forEach((tool, index) => {
        if (tool.isMarkedNotDelivered === true) {
          unselectedIndices.push(index);
        } else {
          selectedIndices.push(index);
        }
      });

      setSelectedTools(selectedIndices);
      setUnselectedTools(unselectedIndices);
    } else {
      const deliverableTools = tools.filter(tool => tool?.isMarkedNotDelivered !== true);
      const notDeliveredToolIds = tools
        .filter(tool => tool?.isMarkedNotDelivered === true && Number.isInteger(tool?.productId))
        .map(tool => tool.productId as number);
      setAvailableTools(deliverableTools);
      setMarkedNotDeliveredToolIds(notDeliveredToolIds);
    }

    hasInitializedRef.current = true;
  };

  const renderContent = () => {
    switch (currentStep) {
      case SECTIONS_STEPS.SELECTION:
        return (
          <SelectedToolsSection
            closeModalCallback={closeModalCallback}
            cardData={cardData}
            isUpdateTools={isUpdateTools}
          />
        );
      case SECTIONS_STEPS.ADD_ADDITIONAL_TOOLS:
        return (
          <AddAdditionalToolsSection
            closeModalCallback={closeModalCallback}
            cardData={cardData}
            serviceDetails={serviceDetails}
            isUpdateTools={isUpdateTools}
          />
        );
      case SECTIONS_STEPS.CONFIRMATION:
        return (
          <ConfirmationSection
            closeModalCallback={closeModalCallback}
            serviceDetails={serviceDetails}
            closeModalAfterSubmitCallback={closeModalAfterSubmitCallback}
            isUpdateTools={isUpdateTools}
          />
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    initializeToolsState(serviceDetails?.tools, isUpdateTools);
  }, [serviceDetails?.tools, isUpdateTools]);

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  return (
    <NudosGenericModal
      closeModalCallback={closeModalCallback}
      modalContent={
        <NudosModalContent
          toCloseModal={closeModalCallback}
          customWidth="780px"
          customHeight="680px"
          excludeHeader
          CustomModalComponent={renderContent()}
        />
      }
    />
  );
};
